import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import { GLOBAL_DASHBOARD_ID } from '../store/CapacityPlannerStore';
import { DashboardRecord } from '../store/types';
import styles from './cpDashboardListing.module.scss';

// ---------------------------------------------------------------------------
// Private dashboard card
// ---------------------------------------------------------------------------

const PrivateCard = observer(({ record }: { record: DashboardRecord }) => {
  const store = useCapacityStore();

  const handleOpen = () => {
    store.setActiveView('dashboard');
    store.openDashboard(record.id);
  };

  return (
    <div className={styles.privateCard}>
      <div className={styles.privateCardTop}>
        <span className={record.isPublished ? styles.sharedBadge : styles.draftBadge}>
          {record.isPublished ? 'SHARED' : 'DRAFT'}
        </span>
        <span className={styles.cardOwner}>{record.ownedBy}</span>
      </div>
      <div className={styles.cardName}>{record.name}</div>
      {record.description && (
        <div className={styles.cardDesc}>{record.description}</div>
      )}
      <div className={styles.cardMeta}>
        <i className="pi pi-calendar" /> {record.updatedAt}
        &nbsp;·&nbsp;
        <i className="pi pi-code-branch" /> {record.versions.length} version{record.versions.length !== 1 ? 's' : ''}
      </div>
      <button className={styles.cardOpenBtn} onClick={handleOpen}>
        Open <i className="pi pi-arrow-right" />
      </button>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main listing component
// ---------------------------------------------------------------------------

interface Props {
  onCreateClick: () => void;
}

const CPDashboardListing = observer(({ onCreateClick }: Props) => {
  const store = useCapacityStore();
  const globalRecord = store.dashboardRecords.find((d) => d.id === GLOBAL_DASHBOARD_ID);
  const privateRecords = store.dashboardRecords.filter(
    (d) => d.id !== GLOBAL_DASHBOARD_ID && !d.isPublished,
  );
  const sharedRecords = store.dashboardRecords.filter(
    (d) => d.id !== GLOBAL_DASHBOARD_ID && d.isPublished,
  );

  const [selectedVersionId, setSelectedVersionId] = useState<string>(
    globalRecord?.activeVersionId ?? '',
  );

  const handleOpenGlobal = () => {
    if (!globalRecord) return;
    const vid = selectedVersionId || globalRecord.activeVersionId;
    store.setActiveView('dashboard');
    store.openDashboard(globalRecord.id, vid);
  };

  return (
    <div className={styles.page}>
      {/* ---- Page header ---- */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Workspaces</h1>
          <p className={styles.subtitle}>Select a planning model or create a private draft to explore scenarios.</p>
        </div>
        <button className={styles.newBtn} onClick={onCreateClick}>
          <i className="pi pi-plus" /> New Dashboard
        </button>
      </div>

      {/* ---- Global Dashboard (featured) ---- */}
      {globalRecord && (
        <section className={styles.globalSection}>
          <div className={styles.sectionHeader}>
            <i className="pi pi-globe" style={{ color: '#7c3aed' }} />
            <span>Global Dashboard</span>
          </div>
          <div className={styles.globalCard}>
            <div className={styles.globalCardHeader}>
              <div className={styles.globalCardTitleRow}>
                <i className="pi pi-chart-bar" />
                <span className={styles.globalCardName}>{globalRecord.name}</span>
              </div>
              <span className={styles.publicBadge}>
                <i className="pi pi-globe" /> PUBLIC
              </span>
            </div>
            <div className={styles.globalCardBody}>
              <p className={styles.globalDesc}>{globalRecord.description}</p>

              {/* Version selector */}
              <div className={styles.versionRow}>
                <span className={styles.versionLabel}>Choose version:</span>
                {globalRecord.versions.map((ver) => {
                  const isActive = (selectedVersionId || globalRecord.activeVersionId) === ver.versionId;
                  return (
                    <button
                      key={ver.versionId}
                      className={`${styles.versionPill} ${isActive ? styles.versionPillActive : ''}`}
                      onClick={() => setSelectedVersionId(ver.versionId)}
                      title={ver.description}
                    >
                      {isActive && <span className={styles.activeDot}>●&nbsp;</span>}
                      {ver.label}
                    </button>
                  );
                })}
              </div>

              {/* Selected version description */}
              {(() => {
                const vid = selectedVersionId || globalRecord.activeVersionId;
                const ver = globalRecord.versions.find((v) => v.versionId === vid);
                return ver ? (
                  <p className={styles.versionDesc}>
                    <i className="pi pi-info-circle" /> {ver.description} &nbsp;·&nbsp; Start: {ver.startYearMonth}
                  </p>
                ) : null;
              })()}

              <div className={styles.globalCardFooter}>
                <span className={styles.globalMeta}>
                  <i className="pi pi-clock" /> Updated {globalRecord.updatedAt}
                  &nbsp;·&nbsp;
                  <i className="pi pi-code-branch" /> {globalRecord.versions.length} versions
                </span>
                <button className={styles.openGlobalBtn} onClick={handleOpenGlobal}>
                  Open Dashboard <i className="pi pi-arrow-right" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ---- Shared (published private) dashboards ---- */}
      {sharedRecords.length > 0 && (
        <section>
          <div className={styles.sectionHeader}>
            <i className="pi pi-share-alt" style={{ color: '#059669' }} />
            <span>Shared</span>
            <span className={styles.sectionCount}>{sharedRecords.length}</span>
          </div>
          <div className={styles.cardsGrid}>
            {sharedRecords.map((d) => (
              <PrivateCard key={d.id} record={d} />
            ))}
          </div>
        </section>
      )}

      {/* ---- Private dashboards ---- */}
      <section>
        <div className={styles.sectionHeader}>
          <i className="pi pi-lock" style={{ color: '#64748b' }} />
          <span>My Private Dashboards</span>
          {privateRecords.length > 0 && (
            <span className={styles.sectionCount}>{privateRecords.length}</span>
          )}
        </div>
        <div className={styles.cardsGrid}>
          {privateRecords.map((d) => (
            <PrivateCard key={d.id} record={d} />
          ))}
          {/* Create new card */}
          <button className={styles.createCard} onClick={onCreateClick}>
            <i className="pi pi-plus" style={{ fontSize: 24, color: '#94a3b8' }} />
            <span className={styles.createLabel}>New Dashboard</span>
            <span className={styles.createHint}>Start a private scenario</span>
          </button>
        </div>
        {privateRecords.length === 0 && (
          <p className={styles.emptyHint}>
            No private dashboards yet. Create one above to explore capacity scenarios without affecting the global model.
          </p>
        )}
      </section>
    </div>
  );
});

export default CPDashboardListing;
